import { Component, ViewEncapsulation, computed, inject, effect, signal } from '@angular/core';
import { BaseComponent } from '../../../core/shared/common/base.component';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { ReactiveFormsModule } from '@angular/forms';
import { SocComponent } from './components/soc/soc.component';
import { SocV2Component } from './components/soc-v2/soc.component';
import { MitreAttackComponent } from '../mitreattack/mitreattack.component';
import { EdrComponent } from './components/edr/edr.component';
import { VssComponent } from './components/vss/vss.component';
import { ActivatedRoute } from '@angular/router';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { EsComponent } from './components/es/es.component';
import { User } from 'src/app/core/models/user';
import { UserService } from '../../admin/users/services/user.service';
import { IntegrationService } from '../../../core/services/integration.service';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-analytics',
  standalone: true,
  imports: [
    LayoutModule,
    ReactiveFormsModule,
    SocComponent,
    SocV2Component,
    MitreAttackComponent,
    EdrComponent,
    VssComponent,
    EsComponent,
  ],
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.scss'],
  providers: [UserService],
})
export class AnalyticsComponent extends BaseComponent {
  private route = inject(ActivatedRoute);
  private integrationService = inject(IntegrationService);
  private readonly user = inject(User);

  showEdrTab = signal<boolean>(false);
  showEsTab = signal<boolean>(false);
  orgId: number | null = null;
  showVssTab = signal<boolean>(false);
  /** `?tab=edr` (e.g. CASI's "View in ONE") opens the EDR tab, once we know the org has EDR. */
  private edrRequested = toSignal(this.route.queryParamMap.pipe(map((p) => p.get('tab') === 'edr')), {
    initialValue: false,
  });
  selectEdr = computed(() => this.edrRequested() && this.showEdrTab());

  constructor() {
    super();
    this.initEffect();
  }

  private initEffect() {
    effect(() => {
      this.route.queryParams
        .pipe(
          filter((params) => params['organizationId']),
          switchMap((params) => {
            this.orgId = +params['organizationId'];
            return this.integrationService.getIntegrationsByOrganization(this.orgId).pipe(
              catchError((error) => {
                console.error('Error loading integrations for analytics tabs:', error);
                return of(null);
              })
            );
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((integrationResponse) => {
          // @mfarry - Show SOC V2 and Email Security tabs for beta users: must be in allow list AND be Cyflare org member
          const isAllowedEsUser = this.user.isCyflareUser();
          const isCyflareOrg = this.user.primary_organization?.id === 1;
          this.showEsTab.set(isAllowedEsUser && isCyflareOrg);

          if (!integrationResponse) {
            this.showEdrTab.set(false);
            this.showVssTab.set(false);
            return;
          }

          const integrations = integrationResponse.value || [];

          let is_demo_org = false;
          if (this.user.selected_organization?.is_demo_org) {
            is_demo_org = this.user.selected_organization.is_demo_org;
          }

          const hasSentinelOneMapping = integrations.some(
            (integration) => integration?.name === 'SentinelOne' && integration?.has_tenant_mapping
          );
          this.showEdrTab.set(hasSentinelOneMapping || is_demo_org);

          if (integrations.length === 0) {
            this.showVssTab.set(false);
            return;
          }

          const hasConfiguredVicarius = integrations.some((integration) => {
            const configStatus = integration?.configuration_status as any;
            return integration?.name === 'Vicarius' && configStatus?.id === 2;
          });

          this.showVssTab.set(hasConfiguredVicarius || is_demo_org);
        });
    });
  }
}
